import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, orderBy, query, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../Context/UserContext';
import './Admin.css';
import './Orders.css';

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role } = useUser();
  
  const isDeliveryPage = location.pathname.includes('/delivery');

  const fetchOrders = useCallback(async () => {
    try {
      const ordersQuery = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(ordersQuery);
      let ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter based on role and page
      if (role === 'shipping_company' || role === 'shipping') {
        // Shipping users ONLY see their assigned orders
        ordersData = ordersData.filter(order => order.assignedTo === user.uid);
      } else if (isDeliveryPage && role === 'admin') {
        // Admin on Delivery page: Show only orders that are assigned to a shipping company
        ordersData = ordersData.filter(order => order.assignedTo);
      }

      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [user, role, isDeliveryPage]);

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user, fetchOrders]);

  // eslint-disable-next-line no-unused-vars
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, { status: newStatus });
      fetchOrders(); // Refresh orders list
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const filteredOrders = orders.filter(order => 
    order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customerPhone?.includes(searchTerm) ||
    order.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // eslint-disable-next-line no-unused-vars
  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#f59e0b';
      case 'confirmed': return '#10b981';
      case 'processing': return '#3b82f6';
      case 'shipped': return '#8b5cf6';
      case 'delivered': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h2>{isDeliveryPage ? 'Delivery Management' : 'Orders'}</h2>
          <p>Manage and track customer orders</p>
        </div>
      </div>

      <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '24px' }}>
        <div className="admin-card" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            borderRadius: '50%', 
            backgroundColor: '#e3f1df', 
            color: '#008060', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: '1.5rem'
          }}>
            <ion-icon name="cart-outline"></ion-icon>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#6d7175', fontWeight: 500 }}>Total Orders</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#202223' }}>{orders.length}</div>
          </div>
        </div>
        
        <div className="admin-card" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            borderRadius: '50%', 
            backgroundColor: '#fff4e5', 
            color: '#b98900', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: '1.5rem'
          }}>
            <ion-icon name="time-outline"></ion-icon>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#6d7175', fontWeight: 500 }}>Pending</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#202223' }}>{orders.filter(o => o.status === 'pending').length}</div>
          </div>
        </div>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #e1e3e5', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
            <ion-icon name="search-outline" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#8c9196' }}></ion-icon>
            <input
              type="text"
              className="custom-input"
              placeholder="Search by name, phone, or order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '36px' }}
            />
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6d7175' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px', opacity: 0.5 }}>
              <ion-icon name="cart-outline"></ion-icon>
            </div>
            <h3>No orders found</h3>
            <p>Orders will appear here when customers make purchases</p>
          </div>
        ) : (
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Fulfillment</th>
                  <th>Items</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => (
                  <tr 
                    key={order.id} 
                    onClick={() => navigate(`/admin/orders/${order.id}`)} 
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ fontWeight: 600 }}>#{order.id.slice(-8)}</td>
                    <td style={{ color: '#6d7175' }}>
                      {order.createdAt?.toDate ? 
                        new Date(order.createdAt.toDate()).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : 
                        new Date(order.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })
                      }
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{order.customerName}</div>
                    </td>
                    <td>EGP {order.totalAmount}</td>
                    <td>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: order.paymentStatus === 'paid' ? '#def7ec' : '#fff4e5',
                        color: order.paymentStatus === 'paid' ? '#03543f' : '#b98900'
                      }}>
                        <span style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: 'currentColor'
                        }}></span>
                        {order.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: order.status === 'delivered' ? '#def7ec' : '#f3f4f6',
                        color: order.status === 'delivered' ? '#03543f' : '#4b5563'
                      }}>
                        <span style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: 'currentColor'
                        }}></span>
                        {order.status === 'delivered' ? 'Fulfilled' : 'Unfulfilled'}
                      </span>
                    </td>
                    <td style={{ color: '#6d7175' }}>{order.items?.length || 0} items</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
